import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  OutlinedInput,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// ── Design System Imports ─────────────────────────────────────────────────────
import typography from "../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
  inputColor,
} from "../../cssUtils/constants";

// ── Shared Components ─────────────────────────────────────────────────────────
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import BackButton from "../../common/back-button";

import {POST_RCS_BOTS_VERIFICATION} from "../../config/urls";

// ── API Imports ───────────────────────────────────────────────────────────────
import axios from "axios";

const tenant = localStorage.getItem("tenant");

// ─────────────────────────────────────────────────────────────────────────────
// Design Tokens — sourced 100% from constants.js / typography.js
// ─────────────────────────────────────────────────────────────────────────────
const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    ...typography.bodyLarge,
    color: inputColor,
    backgroundColor: ColorsConstants.primaryWhite,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.inputFieldGrey },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryBlue100 },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.error },
  },
  "& .MuiInputBase-input": { ...typography.bodyLarge, color: inputColor, padding: "9px 12px" },
  "& .MuiFormHelperText-root": { ...typography.bodySmall, marginLeft: 0, marginTop: "4px" },
};

const MULTILINE_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    ...typography.bodyLarge,
    color: inputColor,
    backgroundColor: ColorsConstants.primaryWhite,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.inputFieldGrey },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryBlue100 },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.error },
  },
  "& .MuiInputBase-input": { ...typography.bodyLarge, color: inputColor },
};

const SELECT_SX = {
  height: 40,
  borderRadius: "8px",
  ...typography.bodyLarge,
  color: inputColor,
  backgroundColor: ColorsConstants.primaryWhite,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.inputFieldGrey },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryGray50 },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ColorsConstants.primaryBlue100 },
  "& .MuiSelect-select": { ...typography.bodyLarge, color: inputColor, padding: "9px 12px" },
};

const SECTION_CARD_SX = {
  backgroundColor: ColorsConstants.primaryWhite,
  border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
  borderRadius: "10px",
  p: 3,
  mb: 2.5,
};

const RADIO_SX = {
  color: ColorsConstants.inputFieldGrey,
  "&.Mui-checked": { color: ColorsConstants.primaryPurple100 },
  padding: "4px 8px 4px 0",
};

// ─────────────────────────────────────────────────────────────────────────────
// Primitive Components
// ─────────────────────────────────────────────────────────────────────────────
function FieldLabel({ children, required, hint }) {
  return (
    <Box sx={{ mb: 0.75, display: "flex", alignItems: "flex-start", gap: 0.5 }}>
      {required && (
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: ColorsConstants.error, flexShrink: 0, mt: "5px" }} />
      )}
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: inputColor, lineHeight: 1.5 }}>
        {children}
        {required && (
          <Typography component="span" sx={{ color: ColorsConstants.error, ml: 0.25 }}>*</Typography>
        )}
      </Typography>
      {hint && (
        <InfoOutlinedIcon sx={{ fontSize: 14, color: grayFontColor, ml: 0.25, cursor: "help", mt: "3px", flexShrink: 0 }} />
      )}
    </Box>
  );
}

function FieldHint({ children }) {
  return (
    <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mb: 1, lineHeight: 1.6 }}>
      {children}
    </Typography>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// KYC Document Types
// ─────────────────────────────────────────────────────────────────────────────
const KYC_TYPES = [
  { id: 1, label: "PAN Card" },
  { id: 2, label: "GST Document" },
  { id: 3, label: "Certificate of Incorporation" },
  { id: 4, label: "Trade License" },
  { id: 5, label: "MSME Certificate" },
  { id: 6, label: "Utility Bill" },
  { id: 7, label: "Bank Statement" },
  { id: 8, label: "Aadhar Card" },
  { id: 9, label: "Passport" },
  { id: 10, label: "Voter ID" },
];

// ─────────────────────────────────────────────────────────────────────────────
// File Upload Zone
// ─────────────────────────────────────────────────────────────────────────────
function FileUploadZone({ onFiles, accept, multiple, maxFiles, currentCount, label, sublabel, error }) {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);
  const atMax = maxFiles && currentCount >= maxFiles;

  const handleFiles = (files) => {
    if (atMax) return;
    onFiles(Array.from(files));
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
      />
      <Box
        onClick={() => !atMax && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!atMax) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        sx={{
          border: `1.5px dashed ${error ? ColorsConstants.error : (dragging ? ColorsConstants.primaryPurple100 : ColorsConstants.inputFieldGrey)}`,
          borderRadius: "8px",
          backgroundColor: dragging ? ColorsConstants.primaryPurple60 : dashboardCardBgColor,
          p: "20px 16px",
          textAlign: "center",
          cursor: atMax ? "not-allowed" : "pointer",
          opacity: atMax ? 0.55 : 1,
          transition: "all 0.15s",
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: 30, color: grayFontColor, mb: 0.5 }} />
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: atMax ? grayFontColor : inputColor }}>
          {atMax ? "Maximum files reached" : label}
        </Typography>
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.25 }}>{sublabel}</Typography>
        {!atMax && (
          <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryPurple100, mt: 0.75, fontWeight: 600, textDecoration: "underline" }}>
            Browse Files
          </Typography>
        )}
      </Box>
      {error && (
        <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP 1 — Bot Details & Experience
// ─────────────────────────────────────────────────────────────────────────────
function Step1BotDetails({ form, setForm, errors }) {
  const f = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  return (
    <Box>
      {/* ── Bot Interactions ──────────────────────────────────────────── */}
      <Box sx={SECTION_CARD_SX}>
        <Typography sx={{ ...typography.h2, color: inputColor, mb: 2.5 }}>Bot Interactions</Typography>

        <Box sx={{ mb: 3 }}>
          <FieldLabel required hint>What actions trigger messages to users?</FieldLabel>
          <FieldHint>
            These are the messages/actions that are triggered to the user. (Are messages sent at a consistent date/time, do users initiate [just-in-time] trigger messages, or do actions trigger [such as package ship/out] trigger messages?)
          </FieldHint>
          <TextField 
            multiline 
            rows={3} 
            fullWidth 
            value={form.trigger_action_desc} 
            onChange={f("trigger_action_desc")}
            placeholder="Describe how messages are triggered to users..." 
            sx={MULTILINE_SX}
            error={!!errors.trigger_action_desc} 
            helperText={errors.trigger_action_desc} 
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <FieldLabel required hint>What types of interactions will this bot have with users?</FieldLabel>
          <FieldHint>
            What are the primarily primary [those that occur most commonly] and secondary [those that are possible but less common] interactions?
          </FieldHint>
          <TextField 
            multiline 
            rows={3} 
            fullWidth 
            value={form.bot_interaction_types} 
            onChange={f("bot_interaction_types")}
            placeholder="Describe primary and secondary interaction types..." 
            sx={MULTILINE_SX}
            error={!!errors.bot_interaction_types} 
            helperText={errors.bot_interaction_types} 
          />
        </Box>

        <Box>
          <FieldLabel required hint>Does this bot support Conversational Part (Does it respond to RCS messages from users)?</FieldLabel>
          <RadioGroup 
            row 
            value={form.is_conversational_supported ? "yes" : "no"}
            onChange={(e) => setForm((p) => ({ ...p, is_conversational_supported: e.target.value === "yes" }))}
            sx={{ mt: 0.5 }}
          >
            {["Yes", "No"].map((opt) => (
              <FormControlLabel 
                key={opt} 
                value={opt.toLowerCase()}
                control={<Radio size="small" sx={RADIO_SX} />}
                label={<Typography sx={typography.bodyLarge}>{opt}</Typography>} 
              />
            ))}
          </RadioGroup>
        </Box>
      </Box>

      {/* ── Consent Management ────────────────────────────────────────── */}
      <Box sx={SECTION_CARD_SX}>
        <Typography sx={{ ...typography.h2, color: inputColor, mb: 2.5 }}>Consent Management</Typography>

        <Box sx={{ mb: 3 }}>
          <FieldLabel required hint>How do you ensure consent, so that only the users who have opted-in receive messages from your bot?</FieldLabel>
          <FieldHint>
            Opt-in is either by logging into the app/web. The information is for how opt-in is acquired.
            If opt-in is through a native/external app, kindly, provide the link.
          </FieldHint>
          <TextField 
            multiline 
            rows={3} 
            fullWidth 
            value={form.opt_in_message} 
            onChange={f("opt_in_message")}
            placeholder="Describe how user consent and opt-in is managed..." 
            sx={MULTILINE_SX}
            error={!!errors.opt_in_message} 
            helperText={errors.opt_in_message} 
          />

          <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: dashboardCardBgColor, borderRadius: "8px", border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}` }}>
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
              All agents must only send to users request the opt-out (e.g., 'STOP' message or equivalent).
            </Typography>
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}>
              This platform processes and enforces opt-out requests. After a user opts out, any GSM messages from the agent will not be blocked by this platform.
            </Typography>
            <Typography 
              component="a" 
              href="#"
              sx={{ ...typography.bodySmall, color: ColorsConstants.primaryPurple100, fontWeight: 600, mt: 0.75, display: "block", cursor: "pointer" }}
            >
              Click here to learn on the Opt-Out Experience
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <FieldLabel required hint>What are the opt-out keywords for this bot?</FieldLabel>
          <FieldHint>
            By default, platform recognizes 'STOP' as the opt-out keyword. If you have facilitated opt-out keywords for opt-out, please specify them (comma separated list, if configuring multiple additional keywords).
          </FieldHint>
          <TextField 
            fullWidth 
            value={form.opt_out_keyword} 
            onChange={f("opt_out_keyword")}
            placeholder="STOP, UNSUBSCRIBE..." 
            sx={FIELD_SX}
            error={!!errors.opt_out_keyword} 
            helperText={errors.opt_out_keyword} 
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <FieldLabel required hint>What message does the bot send when a user opts out?</FieldLabel>
          <FieldHint>
            Upon receipt of Opt-out request, the platform response in the default message.
            If you must use a custom response message for opt-out request, please specify it below.
          </FieldHint>
          <TextField 
            multiline 
            rows={3} 
            fullWidth 
            value={form.opt_out_message} 
            onChange={f("opt_out_message")}
            placeholder="Message sent on opt-out..." 
            sx={MULTILINE_SX}
            error={!!errors.opt_out_message} 
            helperText={errors.opt_out_message} 
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <FieldLabel required hint>What keywords sent to the user to restart and start receiving messages again?</FieldLabel>
          <FieldHint>
            Upon user sending these keywords the platform recognizes 'STOP' as the keyword for re-opt-in request.
            If you must add opt-in keyword responses for sending, please specify them (comma separated list, if configuring multiple opt-out keywords).
          </FieldHint>
          <TextField 
            fullWidth 
            value={form.revoke_opt_out} 
            onChange={f("revoke_opt_out")}
            placeholder="START, SUBSCRIBE..." 
            sx={FIELD_SX} 
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FieldLabel required>Do you want the platform to handle when a user re-subscribe opt-out?</FieldLabel>
          <FieldHint>
            When a user wants to opt-back in after opt-out, they need to send confirmation message. How do you want this message to be sent?
          </FieldHint>
          <RadioGroup 
            row 
            value={form.is_opt_out_by_platform ? "platform" : "custom"}
            onChange={(e) => setForm((p) => ({ ...p, is_opt_out_by_platform: e.target.value === "platform" }))}
            sx={{ mt: 0.5 }}
          >
            <FormControlLabel 
              value="platform" 
              control={<Radio size="small" sx={RADIO_SX} />}
              label={<Typography sx={typography.bodyLarge}>Define re-subscribe / Opt-back-in message</Typography>} 
            />
            <FormControlLabel 
              value="custom" 
              control={<Radio size="small" sx={RADIO_SX} />}
              label={<Typography sx={typography.bodyLarge}>Bot (Optionally) sends the Opt-in return message</Typography>} 
            />
          </RadioGroup>
        </Box>

        <Box>
          <FieldLabel hint>First message sent when a user re-subscribes?</FieldLabel>
          <FieldHint>
            By default, the platform will send the following opt-in confirmation message: "Thanks for your request. You will start receiving RCS messages again from this Bot."
            If you must use a custom response message for opt-out request, please specify it here.
          </FieldHint>
          <TextField 
            multiline 
            rows={3} 
            fullWidth 
            value={form.revoke_opt_out_message} 
            onChange={f("revoke_opt_out_message")}
            placeholder="Welcome back! You've re-subscribed..." 
            sx={MULTILINE_SX} 
          />
        </Box>
      </Box>

      {/* ── Agent Preview ─────────────────────────────────────────────── */}
      <Box sx={SECTION_CARD_SX}>
        <Typography sx={{ ...typography.h2, color: inputColor, mb: 0.75 }}>Agent Preview</Typography>
        <FieldHint>
          The RCS Business Messaging platform needs to run your agent before it launches. Be prepared to test your agent with their commands, or you can create a video showing some functionality and limitations of your agent flow.
        </FieldHint>

        <Box sx={{ mt: 1.5, mb: 3 }}>
          <FieldLabel required>Bot access instructions</FieldLabel>
          <FieldHint>
            To check bot, please send a message to trigger the action (e.g., 'START'). After message is sent, start at the conversation with the user. One data notice for non-launched trigger test bot website relative to be sent into the test phone numbers. If you need to, take screenshots of these times (or related text) and include them.
          </FieldHint>
          <TextField 
            multiline 
            rows={3} 
            fullWidth 
            value={form.bot_access_instructions} 
            onChange={f("bot_access_instructions")}
            placeholder="Step-by-step instructions to access and test the bot..." 
            sx={MULTILINE_SX}
            error={!!errors.bot_access_instructions} 
            helperText={errors.bot_access_instructions} 
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <FieldLabel required hint>RCS Chatbot Webhook</FieldLabel>
          <FieldHint>
            Enter the webhook where you want to receive messages from the RBM Agent.
            NOTE: The webhook needs to be active and should respond with a 200 OK to POST requests.
            Each response must not be available to the Chatbot Webhook endpoint.
          </FieldHint>
          <TextField 
            fullWidth 
            value={form.webhook_url} 
            onChange={f("webhook_url")}
            placeholder="https://yourdomain.com/webhook" 
            type="url" 
            sx={FIELD_SX}
            error={!!errors.webhook_url} 
            helperText={errors.webhook_url} 
          />
        </Box>

        <Box>
          <FieldLabel hint>Video URL (Optional)</FieldLabel>
          <FieldHint>
            If you prefer to use your agent to demo, please provide a screen-recorded video of your agent's core functionality and upload expected files. URLs must be publicly accessible.
          </FieldHint>
          <TextField 
            fullWidth 
            value={form.video_url} 
            onChange={f("video_url")}
            placeholder="https://youtube.com/watch?v=..." 
            type="url" 
            sx={FIELD_SX} 
          />
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP 2 — Brand Details
// ─────────────────────────────────────────────────────────────────────────────
function Step2BrandDetails({ form, setForm, errors, industries = [] }) {
  const f = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  return (
    <Box>
      {/* Brand Information */}
      <Box sx={SECTION_CARD_SX}>
        <Typography sx={{ ...typography.h2, color: inputColor, mb: 2.5 }}>Brand Information</Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <FieldLabel required>Brand Name</FieldLabel>
            <TextField 
              fullWidth 
              value={form.brand_name} 
              onChange={f("brand_name")} 
              placeholder="Acme Corporation" 
              sx={FIELD_SX}
              error={!!errors.brand_name} 
              helperText={errors.brand_name} 
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FieldLabel required>Industry</FieldLabel>
            <FormControl fullWidth size="small" error={!!errors.industry_id}>
              <Select 
                value={form.industry_id || ""} 
                onChange={f("industry_id")} 
                input={<OutlinedInput />} 
                sx={SELECT_SX}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>Select Industry</Typography>;
                  }
                  const industry = industries.find(i => i.id === selected);
                  return <Typography sx={typography.bodyLarge}>{industry?.name || selected}</Typography>;
                }}
              >
                {industries.map((industry) => (
                  <MenuItem key={industry.id} value={industry.id} sx={typography.bodyLarge}>
                    {industry.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.industry_id && (
                <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, mt: 0.5 }}>
                  {errors.industry_id}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FieldLabel required>Brand Website</FieldLabel>
            <TextField 
              fullWidth 
              value={form.brand_website} 
              onChange={f("brand_website")} 
              placeholder="https://www.yourbrand.com" 
              type="url" 
              sx={FIELD_SX}
              error={!!errors.brand_website} 
              helperText={errors.brand_website} 
            />
          </Grid>
        </Grid>
      </Box>

      {/* Registered Address */}
      <Box sx={SECTION_CARD_SX}>
        <Typography sx={{ ...typography.h2, color: inputColor, mb: 2.5 }}>Registered Address</Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <FieldLabel required>Address Line 1</FieldLabel>
            <TextField 
              fullWidth 
              value={form.address_line_1} 
              onChange={f("address_line_1")} 
              placeholder="Building, Street, Area" 
              sx={FIELD_SX}
              error={!!errors.address_line_1} 
              helperText={errors.address_line_1} 
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldLabel required>City</FieldLabel>
            <TextField 
              fullWidth 
              value={form.city} 
              onChange={f("city")} 
              placeholder="Mumbai" 
              sx={FIELD_SX}
              error={!!errors.city} 
              helperText={errors.city} 
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldLabel required>State</FieldLabel>
            <TextField 
              fullWidth 
              value={form.state} 
              onChange={f("state")} 
              placeholder="Maharashtra" 
              sx={FIELD_SX}
              error={!!errors.state} 
              helperText={errors.state} 
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldLabel required>ZIP / Postal Code</FieldLabel>
            <TextField 
              fullWidth 
              value={form.zip_code} 
              onChange={f("zip_code")} 
              placeholder="400001" 
              sx={FIELD_SX}
              error={!!errors.zip_code} 
              helperText={errors.zip_code} 
            />
          </Grid>
        </Grid>
      </Box>

      {/* Primary Contact */}
      <Box sx={SECTION_CARD_SX}>
        <Typography sx={{ ...typography.h2, color: inputColor, mb: 2.5 }}>Primary Contact</Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <FieldLabel required>First Name</FieldLabel>
            <TextField 
              fullWidth 
              value={form.contact_first_name} 
              onChange={f("contact_first_name")} 
              placeholder="John" 
              sx={FIELD_SX}
              error={!!errors.contact_first_name} 
              helperText={errors.contact_first_name} 
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FieldLabel required>Last Name</FieldLabel>
            <TextField 
              fullWidth 
              value={form.contact_last_name} 
              onChange={f("contact_last_name")} 
              placeholder="Doe" 
              sx={FIELD_SX}
              error={!!errors.contact_last_name} 
              helperText={errors.contact_last_name} 
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FieldLabel required>Designation</FieldLabel>
            <TextField 
              fullWidth 
              value={form.contact_designation} 
              onChange={f("contact_designation")} 
              placeholder="CTO, Product Manager..." 
              sx={FIELD_SX}
              error={!!errors.contact_designation} 
              helperText={errors.contact_designation} 
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FieldLabel required>Mobile</FieldLabel>
            <TextField 
              fullWidth 
              value={form.mobile} 
              onChange={f("mobile")} 
              placeholder="+91 98765 43210" 
              sx={FIELD_SX}
              error={!!errors.mobile} 
              helperText={errors.mobile} 
            />
          </Grid>
          <Grid item xs={12}>
            <FieldLabel required>Email Address</FieldLabel>
            <TextField 
              fullWidth 
              value={form.email} 
              onChange={f("email")} 
              placeholder="contact@yourbrand.com" 
              type="email" 
              sx={FIELD_SX}
              error={!!errors.email} 
              helperText={errors.email} 
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP 3 — Business Verification (Media & KYC)
// ─────────────────────────────────────────────────────────────────────────────
function Step3BusinessVerification({ 
  logoFile, setLogoFile, 
  screenImages, setScreenImages, 
  kycFiles, setKycFiles, 
  errors 
}) {
  const handleLogo = (files) => { if (files[0]) setLogoFile(files[0]); };
  const handleScreens = (files) => setScreenImages((p) => [...p, ...files].slice(0, 8));
  const handleKyc = (files) => setKycFiles((p) => [...p, ...files.map((f) => ({ file: f, type: "", typeId: "" }))].slice(0, 10));

  const updateKycType = (i, typeId) => {
    const chosen = KYC_TYPES.find((k) => k.id === parseInt(typeId));
    setKycFiles((p) => p.map((k, idx) => idx === i ? { ...k, typeId: parseInt(typeId), type: chosen?.label || "" } : k));
  };

  const CountBadge = ({ count, max }) => (
    <Typography sx={{
      ...typography.bodySmall, fontWeight: 600,
      px: 1.25, py: 0.25, borderRadius: "12px",
      backgroundColor: count >= max ? "#FFEAEA" : ColorsConstants.primaryBlue60,
      color: count >= max ? ColorsConstants.error : ColorsConstants.primaryPurple100,
      border: `1px solid ${count >= max ? ColorsConstants.error : borderColors.cardBorderPrimaryGrayColor}`,
    }}>
      {count} / {max}
    </Typography>
  );

  return (
    <Box>
      {/* Brand Logo */}
      <Box sx={SECTION_CARD_SX}>
        <Typography sx={{ ...typography.h2, color: inputColor, mb: 2.5 }}>Brand Assets</Typography>

        <Box sx={{ mb: 3.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
            <FieldLabel required>Brand Logo</FieldLabel>
          </Box>
          <FieldHint>Provide a logo for your bot. Recommended: 400×400px. PNG, JPG, SVG.</FieldHint>
          {logoFile ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, backgroundColor: dashboardCardBgColor, borderRadius: "8px", border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}` }}>
              <CheckCircleOutlinedIcon sx={{ fontSize: 20, color: ColorsConstants.primaryPurple100 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: inputColor }}>{logoFile.name}</Typography>
                <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>{(logoFile.size / 1024).toFixed(1)} KB</Typography>
              </Box>
              <ButtonTwo onClick={() => setLogoFile(null)} sx={{ height: 32, ...typography.bodySmall }}>Remove</ButtonTwo>
            </Box>
          ) : (
            <FileUploadZone 
              onFiles={handleLogo} 
              accept="image/*" 
              label="Click or drag to upload logo" 
              sublabel="PNG, JPG, SVG · Recommended 400×400px"
              error={errors.logo}
            />
          )}
          {errors.logo && !logoFile && (
            <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, mt: 1 }}>
              {errors.logo}
            </Typography>
          )}
        </Box>

        {/* Screen Images */}
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
            <FieldLabel required>Screen / Flow Images</FieldLabel>
            <CountBadge count={screenImages.length} max={8} />
          </Box>
          <FieldHint>Upload screenshots showing your bot conversation flows. Max 8 images.</FieldHint>
          <FileUploadZone 
            onFiles={handleScreens} 
            accept="image/*" 
            multiple 
            maxFiles={8} 
            currentCount={screenImages.length}
            label="Drop screenshots of bot flows" 
            sublabel="PNG, JPG · Max 8 images"
            error={errors.screens}
          />
          {errors.screens && screenImages.length === 0 && (
            <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, mt: 1 }}>
              {errors.screens}
            </Typography>
          )}
          {screenImages.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {screenImages.map((file, i) => (
                <Box key={i} sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  p: "8px 12px", borderRadius: "8px",
                  backgroundColor: ColorsConstants.primaryBlue60,
                  border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                  mb: 0.75,
                }}>
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: ColorsConstants.primaryPurple100 }} />
                  <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: inputColor, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {file.name}
                  </Typography>
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>{(file.size / 1024).toFixed(1)} KB</Typography>
                  <Box onClick={() => setScreenImages((p) => p.filter((_, idx) => idx !== i))} sx={{ cursor: "pointer", color: grayFontColor, display: "flex", ml: 0.5 }}>
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* KYC Documents */}
      <Box sx={SECTION_CARD_SX}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
          <Typography sx={{ ...typography.h2, color: inputColor }}>KYC Documents</Typography>
          <CountBadge count={kycFiles.length} max={10} />
        </Box>
        <FieldHint>
          Upload identity documents required for verification. Max 10 files. Each document must have a Document Type selected before submission.
        </FieldHint>

        {errors.kyc && (
          <Alert severity="error" sx={{ mb: 2, ...typography.bodySmall }}>{errors.kyc}</Alert>
        )}

        <Box sx={{ p: 1.5, backgroundColor: "#FFFBEB", border: `1px solid ${ColorsConstants.primaryGray20}`, borderRadius: "8px", mb: 2 }}>
          <Typography sx={{ ...typography.bodySmall, color: "#92400E", fontWeight: 600 }}>
            ⚠️ Each document requires a Document Type selection before submission.
          </Typography>
        </Box>

        <FileUploadZone 
          onFiles={handleKyc} 
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" 
          multiple 
          maxFiles={10} 
          currentCount={kycFiles.length}
          label="Drop KYC documents here" 
          sublabel="PDF, JPG, PNG, DOC · Max 10 files"
        />

        {kycFiles.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            {kycFiles.map((k, i) => (
              <Box key={i} sx={{
                display: "flex", alignItems: "center", gap: 1,
                p: "8px 12px", borderRadius: "8px",
                backgroundColor: ColorsConstants.primaryBlue60,
                border: `1px solid ${k.typeId ? ColorsConstants.primaryPurple100 + "30" : borderColors.cardBorderPrimaryGrayColor}`,
                mb: 0.75,
              }}>
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: ColorsConstants.primaryPurple100, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ ...typography.bodySmall, fontWeight: 600, color: inputColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {k.file.name}
                  </Typography>
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>{(k.file.size / 1024).toFixed(1)} KB</Typography>
                </Box>
                {/* Doc type selector */}
                <FormControl size="small" sx={{ minWidth: 175, flexShrink: 0 }}>
                  <Select 
                    value={k.typeId || ""} 
                    onChange={(e) => updateKycType(i, e.target.value)}
                    input={<OutlinedInput />} 
                    displayEmpty
                    sx={{
                      height: 33, borderRadius: "6px",
                      ...typography.bodySmall,
                      border: `1.5px solid ${k.typeId ? ColorsConstants.primaryPurple100 : "#E8A800"}`,
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiSelect-select": { ...typography.bodySmall, padding: "6px 10px" },
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Typography sx={{ ...typography.bodySmall, color: "#E8A800", fontWeight: 600 }}>Select Type *</Typography>;
                      }
                      const docType = KYC_TYPES.find(t => t.id === selected);
                      return <Typography sx={typography.bodySmall}>{docType?.label}</Typography>;
                    }}
                  >
                    {KYC_TYPES.map((t) => (
                      <MenuItem key={t.id} value={t.id} sx={typography.bodySmall}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box onClick={() => setKycFiles((p) => p.filter((_, idx) => idx !== i))}
                  sx={{ cursor: "pointer", color: grayFontColor, display: "flex", alignItems: "center", flexShrink: 0 }}>
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Wizard Stepper Header
// ─────────────────────────────────────────────────────────────────────────────
const WIZARD_STEPS = [
  "Bot Details and Experience",
  "Brand Details",
  "Business Verification",
];

function WizardStepper({ activeStep }) {
  return (
    <Box sx={{
      backgroundColor: ColorsConstants.primaryWhite,
      borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
      px: 4, py: 1.75,
    }}>
      <Box sx={{ maxWidth: 900, mx: "auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {WIZARD_STEPS.map((label, idx) => {
          const done = idx < activeStep;
          const active = idx === activeStep;
          return (
            <Box key={idx} sx={{ display: "flex", alignItems: "center" }}>
              {idx > 0 && (
                <Box sx={{
                  width: { xs: 20, sm: 40, md: 60 }, height: 1.5, mx: 0.5,
                  backgroundColor: done ? ColorsConstants.primaryPurple100 : ColorsConstants.inputFieldGrey,
                  flexShrink: 0,
                }} />
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{
                  width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                  backgroundColor: done
                    ? ColorsConstants.primaryPurple100
                    : active
                    ? ColorsConstants.primaryBlue100
                    : ColorsConstants.primaryWhite,
                  border: `2px solid ${done || active ? "transparent" : ColorsConstants.inputFieldGrey}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {done ? (
                    <CheckCircleIcon sx={{ fontSize: 14, color: ColorsConstants.primaryWhite }} />
                  ) : (
                    <Typography sx={{
                      ...typography.bodySmall, fontSize: "11px", fontWeight: 700,
                      color: active ? ColorsConstants.primaryWhite : grayFontColor,
                      lineHeight: 1,
                    }}>
                      {idx + 1}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{
                  ...typography.bodySmall,
                  fontWeight: active ? 700 : 500,
                  color: active ? ColorsConstants.primaryBlue100
                    : done ? ColorsConstants.primaryPurple100
                    : grayFontColor,
                  display: { xs: "none", md: "block" },
                  whiteSpace: "nowrap",
                }}>
                  {label}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Success Screen
// ─────────────────────────────────────────────────────────────────────────────
function SuccessScreen({ onDone }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <Box sx={{ textAlign: "center", maxWidth: 460, p: 4 }}>
        <CheckCircleIcon sx={{ fontSize: 56, color: ColorsConstants.primaryPurple100, mb: 1.5 }} />
        <Typography sx={{ ...typography.h1, mb: 1 }}>Verification Submitted!</Typography>
        <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, mb: 3 }}>
          Your bot verification request has been submitted successfully. Our team will review your submission and respond within 2–3 business days.
        </Typography>
        <ButtonOne onClick={onDone}>Done</ButtonOne>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Validation Functions
// ─────────────────────────────────────────────────────────────────────────────
function validateStep(step, form, logoFile, screenImages, kycFiles) {
  const e = {};
  
  if (step === 1) {
    if (!form.trigger_action_desc?.trim()) e.trigger_action_desc = "This field is required";
    if (!form.bot_interaction_types?.trim()) e.bot_interaction_types = "This field is required";
    if (!form.opt_in_message?.trim()) e.opt_in_message = "This field is required";
    if (!form.opt_out_keyword?.trim()) e.opt_out_keyword = "This field is required";
    if (!form.opt_out_message?.trim()) e.opt_out_message = "This field is required";
    if (!form.bot_access_instructions?.trim()) e.bot_access_instructions = "This field is required";
    if (!form.webhook_url?.trim()) e.webhook_url = "This field is required";
    else if (!/^https?:\/\/.+/.test(form.webhook_url)) e.webhook_url = "Please enter a valid URL";
  }
  
  if (step === 2) {
    if (!form.brand_name?.trim()) e.brand_name = "This field is required";
    if (!form.industry_id) e.industry_id = "Please select an industry";
    if (!form.brand_website?.trim()) e.brand_website = "This field is required";
    if (!form.address_line_1?.trim()) e.address_line_1 = "This field is required";
    if (!form.city?.trim()) e.city = "This field is required";
    if (!form.state?.trim()) e.state = "This field is required";
    if (!form.zip_code?.trim()) e.zip_code = "This field is required";
    if (!form.contact_first_name?.trim()) e.contact_first_name = "This field is required";
    if (!form.contact_last_name?.trim()) e.contact_last_name = "This field is required";
    if (!form.contact_designation?.trim()) e.contact_designation = "This field is required";
    if (!form.mobile?.trim()) e.mobile = "This field is required";
    if (!form.email?.trim()) e.email = "This field is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Please enter a valid email";
  }
  
  if (step === 3) {
    if (!logoFile) e.logo = "Brand logo is required";
    if (screenImages.length === 0) e.screens = "At least one screen image is required";
    if (kycFiles.length === 0) e.kyc = "At least one KYC document is required";
    else if (kycFiles.some((k) => !k.typeId)) e.kyc = "All KYC documents must have a Document Type selected";
  }
  
  return e;
}

// ─────────────────────────────────────────────────────────────────────────────
// Initial Form State
// ─────────────────────────────────────────────────────────────────────────────
const INITIAL_FORM = {
  trigger_action_desc: "",
  bot_interaction_types: "",
  is_conversational_supported: false,
  opt_in_message: "",
  opt_out_keyword: "",
  opt_out_message: "",
  revoke_opt_out: "",
  is_opt_out_by_platform: true,
  revoke_opt_out_message: "",
  bot_access_instructions: "",
  webhook_url: "",
  video_url: "",
  brand_name: "",
  industry_id: "",
  brand_website: "",
  address_line_1: "",
  city: "",
  state: "",
  zip_code: "",
  contact_first_name: "",
  contact_last_name: "",
  contact_designation: "",
  email: "",
  mobile: "",
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN WIZARD COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function BotVerificationWizard({ botId, botDotgoId, onSuccess, onCancel }) {
  // Look how clean this is now! We just use the props directly.
  console.log("🔍 Wizard Mounted. Extracted IDs:", { botId, botDotgoId });

  // ... (keep the rest of your state declarations below)
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [logoFile, setLogoFile] = useState(null);
  const [screenImages, setScreenImages] = useState([]);
  const [kycFiles, setKycFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);

  // Fetch industries on mount
  React.useEffect(() => {
    // const fetchIndustries = async () => {
    //   setLoadingIndustries(true);
    //   try {
    //     const response = await axios.get("/api/industries/");
    //     setIndustries(response.data);
    //   } catch (error) {
    //     console.error("Failed to fetch industries:", error);
    //   } finally {
    //     setLoadingIndustries(false);
    //   }
    // };
    // fetchIndustries();
    const inds = [
      { id: 1, name: "E-commerce" },
      { id: 2, name: "Banking & Finance" },
      { id: 3, name: "Travel & Hospitality" },
      { id: 4, name: "Healthcare" },
      { id: 5, name: "Education" },
      { id: 6, name: "Food & Beverage" },
      { id: 7, name: "Entertainment" },
      { id: 8, name: "Telecom" },
      { id: 9, name: "Real Estate" },
      { id: 10, name: "Automotive" },
    ];
    setIndustries(inds);
  }, []);

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNext = () => {
    const errs = validateStep(activeStep + 1, form, logoFile, screenImages, kycFiles);
    if (Object.keys(errs).length > 0) { 
      setErrors(errs); 
      return; 
    }
    setErrors({});
    setActiveStep((s) => s + 1);
    scrollTop();
  };

  const handleBack = () => {
    setErrors({});
    setActiveStep((s) => s - 1);
    scrollTop();
  };

  const handleSubmit = async () => {
    if (!botId) {
      setSubmitError("Critical Error: Missing Bot ID. Please navigate back and try again.");
      return;
    }
    // Validate final step
    const errs = validateStep(3, form, logoFile, screenImages, kycFiles);
    if (Object.keys(errs).length > 0) { 
      setErrors(errs); 
      return; 
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const submitData = new FormData();

      // Append physical files
      if (logoFile) submitData.append("brandLogoImage", logoFile);
      screenImages.forEach((file) => submitData.append("screenImages", file));
      kycFiles.forEach((k) => submitData.append("kycdocs", k.file));

      // Build JSON payload matching backend expectations
      const jsonPayload = {
        bot_id: botDotgoId,
        trigger_action: form.trigger_action_desc,
        bot_access_instructions: form.bot_access_instructions,
        bot_interaction_types: form.bot_interaction_types,
        video_url: form.video_url || null,
        is_conversational_supported: form.is_conversational_supported,
        opt_in_message: form.opt_in_message,
        opt_out_message: form.opt_out_message,
        is_opt_out_by_platform: form.is_opt_out_by_platform,
        opt_out_keyword: form.opt_out_keyword || null,
        revoke_opt_out: form.revoke_opt_out || null,
        revoke_opt_out_message: form.revoke_opt_out_message || null,
        webhook_url: form.webhook_url,
        brand_details: {
          brand_name: form.brand_name,
          industry_id: parseInt(form.industry_id),
          brand_website: form.brand_website,
          address: {
            address_line_1: form.address_line_1,
            city: form.city,
            state: form.state,
            zip_code: form.zip_code,
          },
          brand_emails_json: [{
            first_name: form.contact_first_name,
            last_name: form.contact_last_name,
            designation: form.contact_designation,
            email: form.email,
            mobile: form.mobile,
          }],
        },
        screen_image: screenImages.map((f) => ({ image_name: f.name })),
        kyc_details: kycFiles.map((doc) => ({
          document_type: doc.type,
          document_name: doc.file.name.replace(/\.[^/.]+$/, ""),
          file_name: doc.file.name,
          document_type_id: doc.typeId,
        })),
      };

      // Append JSON data
      submitData.append("data", JSON.stringify(jsonPayload));

      const apiUrl = POST_RCS_BOTS_VERIFICATION.replace('<int:pk>', botId);

      // Make API call to backend
      const response = await axios.post(
        apiUrl,
        submitData,
        {
          headers: {
            "X-Tenant-ID": tenant
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        setSubmitted(true);
        onSuccess?.();
      } else {
        throw new Error(response.data?.error || "Submission failed");
      }
    } catch (error) {
      console.error("Verification submission error:", error);
      setSubmitError(
        error.response?.data?.error || 
        error.response?.data?.details || 
        error.message || 
        "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <SuccessScreen onDone={onCancel} />;
  }

  const errorCount = Object.keys(errors).length;
  const isLastStep = activeStep === WIZARD_STEPS.length - 1;

  return (
    <Box sx={{ backgroundColor: ColorsConstants.primaryBlue60, minHeight: "100vh", fontFamily: "'Lexend', 'Roboto', sans-serif" }}>
      {/* Stepper Header */}
      <WizardStepper activeStep={activeStep} />

      {/* Body */}
      <Box sx={{ maxWidth: 860, mx: "auto", px: 3, pt: 3, pb: "90px" }}>
        {/* Page heading */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ ...typography.h1, mb: 0.5 }}>
            {WIZARD_STEPS[activeStep]}
          </Typography>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor, maxWidth: 820 }}>
            {activeStep === 0 && "These questions will be used to verify the business integrity of the bot, must be verified by each sender. The questions on this page help the verifier understand and evaluate the bot behaviour."}
            {activeStep === 1 && "Provide your brand identity, registered address, and primary contact information for verification."}
            {activeStep === 2 && "Upload your brand assets and KYC documents required for business verification."}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3, borderColor: borderColors.cardBorderPrimaryGrayColor }} />

        {/* Validation banner */}
        {errorCount > 0 && (
          <Alert severity="error" sx={{ mb: 2.5, ...typography.bodySmall, borderRadius: "8px" }}>
            Please fix {errorCount} error{errorCount > 1 ? "s" : ""} on this step before continuing.
          </Alert>
        )}

        {/* Step panels */}
        {activeStep === 0 && (
          <Step1BotDetails 
            form={form} 
            setForm={setForm} 
            errors={errors} 
          />
        )}
        {activeStep === 1 && (
          <Step2BrandDetails 
            form={form} 
            setForm={setForm} 
            errors={errors}
            industries={industries}
            loadingIndustries={loadingIndustries}
          />
        )}
        {activeStep === 2 && (
          <Step3BusinessVerification
            logoFile={logoFile} 
            setLogoFile={setLogoFile}
            screenImages={screenImages} 
            setScreenImages={setScreenImages}
            kycFiles={kycFiles} 
            setKycFiles={setKycFiles}
            errors={errors}
          />
        )}
      </Box>

      {/* Sticky Footer */}
      <Box sx={{
        position: "fixed", 
        bottom: 0, 
        left: 0, 
        right: 0,
        backgroundColor: ColorsConstants.primaryWhite,
        borderTop: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
        px: 4, 
        py: 1.5, 
        zIndex: 100,
        boxShadow: "0 -2px 10px rgba(8,36,92,0.07)",
      }}>
        <Box sx={{ maxWidth: 860, mx: "auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <ButtonOne 
            variant="outlined" 
            onClick={activeStep === 0 ? onCancel : handleBack} 
            disabled={submitting}
          >
            Back
          </ButtonOne>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {submitError && (
              <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, maxWidth: 340 }}>
                ⚠️ {submitError}
              </Typography>
            )}
            {!isLastStep ? (
              <ButtonOne onClick={handleNext}>Next</ButtonOne>
            ) : (
              <ButtonOne onClick={handleSubmit} disabled={submitting} sx={{ minWidth: 200 }}>
                {submitting ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={15} sx={{ color: ColorsConstants.primaryWhite }} />
                    <Typography sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryWhite }}>Submitting...</Typography>
                  </Box>
                ) : "Submit for Verification"}
              </ButtonOne>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}